/**
 * Mock drop-in de `next/navigation`.
 *
 * Reemplaza `useRouter`, `usePathname`, `useSearchParams` por equivalentes
 * sobre TanStack Router. Los componentes migrados importan desde aquí:
 *   import { useRouter, usePathname } from '@/lib/mocks/router'
 */

import { useNavigate, useLocation, useRouter as useTanstackRouter } from '@tanstack/react-router'

export interface MockRouter {
  push: (href: string) => void
  replace: (href: string) => void
  refresh: () => void
  back: () => void
}

export function useRouter(): MockRouter {
  const navigate = useNavigate()
  const tsRouter = useTanstackRouter()

  return {
    push: (href: string) => {
      void navigate({ to: href })
    },
    replace: (href: string) => {
      void navigate({ to: href, replace: true })
    },
    refresh: () => {
      void tsRouter.invalidate()
    },
    back: () => {
      window.history.back()
    },
  }
}

export function usePathname(): string {
  const location = useLocation()
  return location.pathname
}

export function useSearchParams(): URLSearchParams {
  const _location = useLocation()
  // location.search es un objeto en TanStack Router; lo convertimos a URLSearchParams
  if (typeof window !== 'undefined') {
    return new URLSearchParams(window.location.search)
  }
  return new URLSearchParams()
}

/**
 * Drop-in de `useParams()` de `next/navigation` (Next App Router).
 * En Next, devuelve un objeto con las dynamic params del segmento actual.
 * Acá leemos la URL para extraer el segmento dinámico cuando aplique.
 */
export function useParams<T extends Record<string, string | undefined>>(): T {
  if (typeof window === 'undefined') return {} as T
  const segments = window.location.pathname.split('/').filter(Boolean)
  // En la demo, la única ruta con param es /dashboard/user/projects/:id
  const projectsIdx = segments.indexOf('projects')
  if (projectsIdx !== -1 && segments[projectsIdx + 1]) {
    return { id: segments[projectsIdx + 1] } as unknown as T
  }
  return {} as T
}
